import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { DataNudge } from '../components/DataNudge.tsx';
import { DebugOverlay, ErrorBoundary } from '../components/Diagnostics.tsx';
import { breadcrumb } from '../diag/monitor.ts';
import { diag } from '../diag/log.ts';
import { TabBar } from '../components/ui.tsx';
import { RepoContext, useEvents, useProgramme, useRepo } from '../lib/hooks.ts';
import { registerExerciseNames } from '../lib/defaults.ts';
import { backupIfDue } from '../lib/push.ts';
import { loadIndex, loadShared } from '../lib/configs.ts';
import type { Repo } from '../lib/repo.ts';
import { useGate } from '../lib/useGate.ts';
import { Claim, Restoring, SignIn, Welcome } from '../screens/Gate.tsx';
import { Onboarding } from '../screens/Onboarding.tsx';
import { Ready } from '../screens/Ready.tsx';
import { Session } from '../screens/Session.tsx';

// Off the workout path: split out so Train loads first.
const History = lazy(() => import('../screens/History.tsx').then((m) => ({ default: m.History })));
const Progress = lazy(() => import('../screens/Progress.tsx').then((m) => ({ default: m.Progress })));
const Settings = lazy(() => import('../screens/Settings.tsx').then((m) => ({ default: m.Settings })));

function Shell({ updateReady, applyUpdate }: { updateReady: boolean; applyUpdate: () => void }) {
  const events = useEvents();
  const programme = useProgramme(events);
  useEffect(() => {
    // Hosted-plan exercises carry their display name in the workout snapshot; make History/Progress/Session use it.
    if (events) registerExerciseNames(Object.fromEntries(events.flatMap((e) => (e.type === 'workout_started' ? e.prescriptions.map((p) => [p.exercise, p.name]) : []))));
  }, [events]);
  const { pathname } = useLocation();
  useEffect(() => {
    breadcrumb(`route ${pathname}`);
    diag('nav', 'route', { path: pathname.replace(/[0-9a-f-]{20,}/g, ':id') });
  }, [pathname]);
  const repo = useRepo();
  const { gate, retryRestore } = useGate(programme);
  const [signingIn, setSigningIn] = useState(false);
  const inApp = gate.kind === 'app';
  useEffect(() => {
    if (gate.kind !== 'welcome') setSigningIn(false); // after a later sign-out, start again from Welcome
  }, [gate.kind]);
  useEffect(() => {
    if (inApp && repo.user()) backupIfDue(repo); // Worker skips it if a backup is under 6 days old
  }, [inApp, repo]);
  useEffect(() => {
    // Basement rule: while online, keep the small shared configs (guides, RPE table, catalogue) on the device.
    if (inApp) void loadIndex(repo.store).then(({ index, fresh }) => (fresh ? loadShared(repo.store, index) : null)).catch(() => undefined);
  }, [inApp, repo]);

  // Outside a live session an update can always be offered, including on the gate and onboarding screens (J10).
  const banner = updateReady ? (
    <div className="bg-surface-overlay text-sm p-3 flex justify-between items-center" role="status">
      A new version is ready.
      <button type="button" className="text-now-ink min-h-11 px-3" onClick={applyUpdate}>
        Reload
      </button>
    </div>
  ) : null;
  const pre = (node: React.ReactNode) => (
    <>
      {banner}
      {node}
    </>
  );
  if (gate.kind === 'loading' || !events || !programme) return <main className="p-5 text-text-muted">Loading…</main>;
  if (gate.kind === 'welcome') return pre(signingIn ? <SignIn /> : <Welcome onStart={() => setSigningIn(true)} />);
  if (gate.kind === 'claim') return pre(<Claim />);
  if (gate.kind === 'restoring' || gate.kind === 'restore_failed') return pre(<Restoring failed={gate.kind === 'restore_failed'} onRetry={retryRestore} />);
  if (gate.kind === 'onboarding') return pre(<Onboarding />);

  const live = programme.inProgress[programme.inProgress.length - 1];
  const inSession = !!live && pathname === '/';
  return (
    <>
      {gate.reauth && !inSession && (
        <div className="bg-surface-overlay text-sm p-3 flex justify-between items-center gap-3" role="status">
          <span>You're signed out on this device. Your workouts are saved here and sync when you sign in again.</span>
          <Link to="/settings" className="text-now-ink min-h-11 px-2 grid place-items-center shrink-0">
            Sign in
          </Link>
        </div>
      )}
      {!inSession && banner}
      {!inSession && !gate.reauth && !pathname.startsWith('/settings') && <DataNudge programme={programme} events={events} />}
      <Suspense fallback={<main className="p-5 text-text-muted">Loading…</main>}>
        <Routes>
          <Route path="/" element={live ? <Session key={live} workoutId={live} events={events} /> : <Ready programme={programme} events={events} />} />
          <Route path="/progress/*" element={<Progress programme={programme} events={events} />} />
          <Route path="/history" element={<History events={events} programme={programme} />} />
          <Route path="/settings/*" element={gate.reauth ? <SignIn /> : <Settings programme={programme} />} />
        </Routes>
      </Suspense>
      {!inSession && !pathname.startsWith('/settings') && <TabBar />}
    </>
  );
}

export function App({ repo, updateReady = false, applyUpdate = () => undefined }: { repo: Repo; updateReady?: boolean; applyUpdate?: () => void }) {
  return (
    <RepoContext.Provider value={repo}>
      <ErrorBoundary>
        <Shell updateReady={updateReady} applyUpdate={applyUpdate} />
      </ErrorBoundary>
      <DebugOverlay />
    </RepoContext.Provider>
  );
}
